import { yupResolver } from "@hookform/resolvers/yup";
import { Dialog, DialogContent, DialogTitle, FormControl, Stack } from "@mui/material";
import { useTimeoutEffect } from "@react-hookz/web";
import { type FC, useCallback, useEffect, useRef } from "react";
import { FormProvider, type Resolver, useForm, useWatch } from "react-hook-form";
import type { TagGroup } from "../../../../merge-tag/components/types";
import type { EditorLinkData } from "../../plugins/toolbar/hooks/use-link-button-on-click";
import { getSurveyIdFromUrl } from "../utils/survey-url";
import { LinkFormActions } from "./link-form-actions";
import { LinkSurveyField } from "./link-survey-field";
import { LinkTextField } from "./link-text-field";
import { LinkUrlField } from "./link-url-field";
import { defaultSchema, type Link, rangeAddSchema } from "./types";

export const LinkForm: FC<{
  onRemove: () => void;
  onCancel: () => void;
  onConfirm: (link: Link) => void;
  disabled?: boolean;
  filterTags: (x: string) => TagGroup;
  data: EditorLinkData;
}> = ({ onRemove, onCancel, onConfirm, disabled, filterTags, data }) => {
  const { mode, type } = data;

  const inputRef = useRef<HTMLInputElement>(null);

  useTimeoutEffect(() => inputRef.current?.focus(), 100);

  const form = makeForm(data);

  const methods = useForm(form);
  const { handleSubmit, reset, setValue, getValues, control } = methods;

  useWatch({ control, name: "url" });
  const [url] = getValues(["url"]);

  const confirm = useCallback(
    (link: Link) => {
      onConfirm(link);
      reset();
    },
    [onConfirm, reset],
  );

  // The link can only be a survey if the add survey button is clicked in the twilio editor.
  // Or if the user pastes a survey link into the dialog.
  useEffect(() => {
    const survey_id = getSurveyIdFromUrl(url);
    if (survey_id) {
      setValue("text", "survey_link");
    }
  }, [url, setValue]);
  return (
    <FormProvider {...methods}>
      <Dialog
        onClose={onCancel}
        open={true}
        slotProps={{
          paper: {
            sx: {
              minWidth: "400px",
            },
          },
        }}
      >
        <DialogTitle>
          {mode === "add" ? (type === "asset" ? "Insert as link" : "Add link") : "Link"}
        </DialogTitle>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <form onSubmit={handleSubmit(confirm)}>
          <FormControl variant="standard" sx={{ my: 1, minWidth: 300 }} fullWidth>
            <DialogContent sx={{ p: 1 }}>
              <Stack direction="column" spacing={2}>
                <LinkTextField filterTags={filterTags} disabled={disabled} data={{ mode, type }} />
                <LinkUrlField filterTags={filterTags} disabled={disabled} type={type} />
                <LinkSurveyField data={data} />
              </Stack>
            </DialogContent>
            <LinkFormActions onRemove={onRemove} onCancel={onCancel} data={{ mode, url }} />
          </FormControl>
        </form>
      </Dialog>
    </FormProvider>
  );
};

function makeForm({ mode, type, url }: EditorLinkData): {
  defaultValues: Link;
  resolver: Resolver<Link>;
} {
  if (type === "survey_link") {
    return {
      defaultValues: { text: "survey_link", url: url || "" },
      resolver: yupResolver(defaultSchema) as Resolver<Link>,
    };
  }

  if (mode === "add" && ["range", "asset"].includes(type)) {
    return {
      defaultValues: { text: "", url: type === "asset" ? url : "" },
      resolver: yupResolver(rangeAddSchema) as Resolver<Link>,
    };
  }

  return {
    defaultValues: { text: "", url: url ?? "" },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(defaultSchema),
  };
}
